<script lang="ts" setup>
import { Action } from '~/composables/NotificationHandlers';
import { kindByType } from '~/types/API/PushNotification/Enum';

const { handleMessage, handleWorksheet, handleAppointment } = useNotificationHandlers();
const notificationStore = useNotificationStore();

interface Props {
  notification: NotificationOverviewResponseDTO
}
const props = defineProps<Props>();

const kind = computed(() => kindByType[props.notification.NotificationType as keyof typeof kindByType]);

async function handleClick() {
  notificationStore.markAsRead(props.notification.NotificationID);

  let closeMenu = false;

  switch (kind.value) {
    case 'message': {
      handleMessage(props.notification.EntityID, Action.created);
      closeMenu = true;
      break;
    }
    case 'worksheet': {
      handleWorksheet(props.notification.EntityID, Action.created);
      closeMenu = true;
      break;
    }
    case 'appointment': {
      handleAppointment(props.notification.EntityID, Action.created);
      closeMenu = true;
      break;
    }
  }

  if (closeMenu) {
    await menuController.close('notification-menu');
  }

  return;
}

const notificationIcon = computed(() => {
  switch (kind.value) {
    case 'appointment':
      return 'fa-light fa-calendar fa-fw';
    case 'message':
      return 'fa-light fa-envelope fa-fw';
    case 'worksheet':
      return 'fa-light fa-clipboard fa-fw';
    default:
      return '';
  }
});

</script>

<template>
  <div
    class="notification-wrapper"
    :class="{ 'unread-wrapper': notification.ReadDate === null }"
  >
    <IonItem
      lines="full"
      button
      class="notification-item"
      :class="{ 'unread-item': notification.ReadDate === null }"
      @click="handleClick"
    >
      <div
        slot="start"
        class="icon-corner"
        :class="{ 'unread': notification.ReadDate === null }"
      >
        <IonIcon :class="notificationIcon" />
        <div
          v-if="notification.ReadDate === null"
          class="unread-dot"
        />
      </div>
      
      <div class="content-wrapper">
        <IonLabel>{{ notification.Header }}</IonLabel>
        <IonNote style="font-size:small;">
          {{ notification.Body }}
        </IonNote>
        <IonNote style="font-size:x-small;">
          {{ notification.CreatedDate }}
        </IonNote>
      </div>
    </IonItem>
  </div>
</template>

<style scoped>
.notification-wrapper {
  position: relative;
  margin: 0;
}

.notification-wrapper.unread-wrapper {
  background-color: rgba(var(--ion-color-primary-rgb), 0.1);
}

.notification-item {
  --background: transparent;
  align-items: flex-start; /* Align items to the top */
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 8px;
}

.notification-item.unread-item {
  font-weight: bold;
}

.icon-corner {
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  margin-right: 8px;
  margin-top: 1px;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
}

.unread-dot {
  position: absolute;
  top: 1px;
  right: 0px;
  width: 6px;
  height: 6px;
  background-color: var(--ion-color-primary);
  border-radius: 50%;
}
</style>
